/**
 * @fileoverview Debug logging utility that can be toggled via URL parameter or localStorage
 * @module local-db/utils/debugLog
 *
 * Responsibilities:
 * - Provide conditional debug logging based on debug mode flag
 * - Support debug mode toggling via URL parameter (?debug=1) or localStorage
 * - Export both debugLog and debugWarn functions
 *
 * Dependencies:
 * - None (pure utility functions)
 *
 * Architecture:
 * - Debug mode is checked on each call to allow dynamic toggling
 * - URL parameter takes precedence over localStorage
 *
 * Notes:
 * - Debug mode is off by default to reduce console noise in production
 */

/**
 * Checks if debug mode is enabled via URL parameter or localStorage
 * @returns true if debug mode is enabled, false otherwise
 */
function isDebugMode(): boolean {
  // Check URL parameter first
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('debug') && urlParams.get('debug') === '1') {
      return true;
    }
    
    // Check localStorage as fallback
    try {
      return localStorage.getItem('firesquirrel_debug') === 'true';
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Logs messages to console only when debug mode is enabled
 * @param args - Arguments to pass to console.log
 */
export function debugLog(...args: unknown[]): void {
  if (isDebugMode()) {
    console.log(...args);
  }
}

/**
 * Logs warning messages to console only when debug mode is enabled
 * @param args - Arguments to pass to console.warn
 */
export function debugWarn(...args: unknown[]): void {
  if (isDebugMode()) {
    console.warn(...args);
  }
}